import type { ListMethod } from '@/types/list-method';

/**
 * Which profiles of the Select profiles step go into the list (#595, board E3).
 *
 * The step used to take every result of the filters; the admin now ticks rows.
 * Two shapes cover it without holding thousands of ids: the rows ticked one by
 * one, or "every result" minus the rows unticked since. Nothing is ticked at
 * first, and a new filter starts the selection over; ticks made against other
 * results would otherwise add profiles nobody sees any more.
 */
export type RowSelection = { mode: 'ids'; ids: string[] } | { mode: 'all'; excludedIds: string[] };

export const EMPTY_SELECTION: RowSelection = { mode: 'ids', ids: [] };

export function selectAllResults(): RowSelection {
  return { mode: 'all', excludedIds: [] };
}

export function isRowSelected(selection: RowSelection, id: string): boolean {
  return selection.mode === 'ids'
    ? selection.ids.includes(id)
    : !selection.excludedIds.includes(id);
}

const without = (ids: string[], id: string) => ids.filter((x) => x !== id);

export function toggleRow(selection: RowSelection, id: string): RowSelection {
  if (selection.mode === 'ids') {
    return {
      mode: 'ids',
      ids: selection.ids.includes(id) ? without(selection.ids, id) : [...selection.ids, id],
    };
  }
  return {
    mode: 'all',
    excludedIds: selection.excludedIds.includes(id)
      ? without(selection.excludedIds, id)
      : [...selection.excludedIds, id],
  };
}

/** The header box: tick or untick every row of the page shown. */
export function setPageSelected(
  selection: RowSelection,
  pageIds: readonly string[],
  selected: boolean,
): RowSelection {
  const onPage = new Set(pageIds);
  if (selection.mode === 'ids') {
    const others = selection.ids.filter((id) => !onPage.has(id));
    return { mode: 'ids', ids: selected ? [...others, ...pageIds] : others };
  }
  const others = selection.excludedIds.filter((id) => !onPage.has(id));
  return { mode: 'all', excludedIds: selected ? others : [...others, ...pageIds] };
}

export function pageSelectionState(
  selection: RowSelection,
  pageIds: readonly string[],
): 'none' | 'some' | 'all' {
  const ticked = pageIds.filter((id) => isRowSelected(selection, id)).length;
  if (ticked === 0) return 'none';
  return ticked === pageIds.length ? 'all' : 'some';
}

/** How many profiles the selection holds, out of `total` results. */
export function selectedCount(selection: RowSelection, total: number): number {
  return selection.mode === 'ids'
    ? selection.ids.length
    : Math.max(0, total - selection.excludedIds.length);
}

/** The fetched results the selection keeps. */
export function keepSelected<T extends { id: string }>(rows: T[], selection: RowSelection): T[] {
  return rows.filter((row) => isRowSelected(selection, row.id));
}

/**
 * Whether the selection must hold someone before the wizard moves on (#641).
 * With "Select existing profiles" as the only source, an empty selection would
 * create an empty list; the admin who set filters and saw the results thought
 * they were in it. Beside a file or a list copy, ticking no one stays allowed.
 */
export function selectionRequired(methods: readonly ListMethod[], count: number): boolean {
  return (
    methods.includes('select') &&
    !methods.includes('file') &&
    !methods.includes('import') &&
    count === 0
  );
}
